import React, { Fragment } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdTextFields } from "react-icons/md";

import { PaziaColorPill } from "components/atoms";
import { PaziaButton, PaziaSelect, PaziaFileDropzone, PaziaInput, PaziaToggleIcon } from "components/atoms";
import { ConfirmDeleteButton } from "components/molecules";
import { appendFileToBrowser } from "utils";
import { DocumentTemplatePageApi, DocumentTemplatePlaceholderApi, documentTemplatesApi } from "api";
import { DocumentPreview } from "./DocumentPreview";
import { useToaster } from "utils";
import { ExpandArrows } from "components/molecules";

interface PagePlaceholdersFormProps {
  onSubmit: (data: PageFormValues) => Promise<void>;
  onRemove: (pageId: number) => void;
  width: string;
  documentTemplateId: number;
  height: string;
  page: DocumentTemplatePageApi;
  templateName: string;
  formClassName: string;
}
export const PagePlaceholdersForm = ({
  onSubmit,
  formClassName,
  documentTemplateId,
  templateName,
  width,
  onRemove,
  height,
  page,
}: PagePlaceholdersFormProps): JSX.Element => {
  const [processingDownload, setProcessingDownload] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const { showError } = useToaster();
  const methods = useForm<PageFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      [FORM_NAMES.PAGE_ID]: page.pageId,
      [FORM_NAMES.PAGE_NUMBER]: 1,
      [FORM_NAMES.BACKGROUND]: page.background,
      // @ts-ignore
      [FORM_NAMES.PLACEHOLDERS]:
        page.placeholders.length > 0 ? page.placeholders.map((p) => ({ ...p, expanded: false })) : [],
    },
  });
  const { handleSubmit, watch, setValue } = methods;
  const { append, remove } = useFieldArray({
    control: methods.control,
    name: FORM_NAMES.PLACEHOLDERS,
    keyName: "key",
  });
  const backgroundImageWatch = watch(FORM_NAMES.BACKGROUND);
  const backgroundImage =
    // @ts-ignore
    typeof backgroundImageWatch === "string" ? backgroundImageWatch : (backgroundImageWatch?.preview ?? "");

  const placeholderWatch = watch().placeholders;

  const download = async () => {
    setProcessingDownload(true);
    try {
      const response = await documentTemplatesApi.download(Number(documentTemplateId));
      appendFileToBrowser(response.data, templateName);
    } catch (e) {
      console.error(e);
      showError("Nie udało się pobrać szablonu");
    } finally {
      setProcessingDownload(false);
    }
  };

  const onSubmitHandler = async (data: PageFormValues) => {
    setProcessing(true);
    try {
      await onSubmit(data);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          className={`flex h-full flex-col overflow-y-scroll border-r border-r-slate-200 px-4 dark:border-r-slate-700 ${formClassName}`}
        >
          {!backgroundImageWatch ? (
            <div className="flex flex-col items-center pt-10">
              <div className="text-2xl font-bold text-slate-700 dark:text-slate-100">Brak tła</div>
              <div className="p-2 italic text-slate-500">Wgraj plik aby kontynuować</div>
              <PaziaFileDropzone name={FORM_NAMES.BACKGROUND} type="image" preview="file" required single />
            </div>
          ) : (
            <Fragment>
              <div className="flex justify-between">
                <PaziaButton
                  btnType="button"
                  type="tertiary"
                  title="Wyczyść tło"
                  onClick={() => setValue(FORM_NAMES.BACKGROUND, "")}
                />
                <div className="flex gap-2">
                  <PaziaButton
                    btnType="button"
                    type="secondary"
                    title="Dodaj HTML"
                    // @ts-ignore
                    onClick={() => append(defaultHtmlPlaceholder)}
                  />
                  <PaziaButton
                    btnType="button"
                    type="secondary"
                    title="Dodaj tekst"
                    // @ts-ignore
                    onClick={() => append(defaultPlaceholder)}
                  />
                  <PaziaButton
                    btnType="button"
                    type="secondary"
                    title="Dodaj obraz"
                    // @ts-ignore
                    onClick={() => append(defaultImagePlaceholder)}
                  />
                </div>
              </div>
              {placeholderWatch.length > 0 ? (
                placeholderWatch.map((x, index) => (
                  <Placeholder key={index} index={index} placeholder={x} onRemove={() => remove(index)} />
                ))
              ) : (
                <div className="flex grow flex-col items-center justify-center">
                  <span className="mb-2 text-2xl font-bold text-slate-700 dark:text-slate-100">Brak placeholderów</span>
                  <div className="mb-6 text-center">
                    Użyj przycisków "Dodaj tekst", "Dodaj obraz" aby dodać pola dynamiczne.
                  </div>
                </div>
              )}
            </Fragment>
          )}
        </form>
        <div
          className={`flex justify-between gap-2 border border-slate-200 bg-slate-100 p-4 dark:border-slate-700 dark:bg-slate-700 ${formClassName}`}
        >
          <div>
            <ConfirmDeleteButton
              title="Usuń stronę"
              dialog={{
                title: "Usuń stronę",
                description: "Czy na pewno chcesz usunąć stronę?",
              }}
              onConfirm={() => onRemove(page.pageId)}
              className="item-start"
            />
          </div>
          <div className="flex gap-2">
            {/* @ts-ignore */}

            <PaziaButton
              type="success"
              disabled={!backgroundImageWatch}
              title="Zapisz"
              // @ts-ignore
              onClick={() => handleSubmit(onSubmitHandler)()}
              processing={processing}
            />
          </div>
          {/* TODO disable */}
        </div>
      </FormProvider>

      {/* <TransformWrapper>
        <TransformComponent> */}
      <div
        className={`fixed right-0 border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 ${formClassName}`}
        style={{
          width: "calc(calc(100% - 16rem)* 0.6)",
          height: "calc(100vh - 174px)",
        }}
      >
        <div
          className="flex items-center justify-between border-b border-b-slate-200 p-4 text-center dark:border-b-slate-700"
          style={{ height: "52.5px" }}
        >
          <span className="text-s font-semibold">Podgląd</span>
          <PaziaButton type="tertiary" title="Pobierz PDF" onClick={download} processing={processingDownload} />
        </div>
        <div
          className="fixed right-0 overflow-auto p-4"
          style={{
            width: "calc(calc(100% - 16rem)* 0.6)",
            height: "calc(100vh - 248px)",
          }}
        >
          <DocumentPreview
            backgroundImage={backgroundImage}
            docHeight={height}
            docWidth={width}
            placeholders={placeholderWatch}
            fillPlaceholdersBackground
          />
        </div>
      </div>
      {/* </TransformComponent>
      </TransformWrapper> */}
    </>
  );
};

interface PlaceholderProps {
  placeholder: PlacehodlerFormValues;
  onRemove: () => void;
  index: number;
}
const Placeholder = ({ onRemove, index, placeholder }: PlaceholderProps) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="mt-2 rounded-lg bg-gray-200 dark:bg-slate-900">
      {expanded ? (
        <div className="grid grid-cols-12 gap-2 px-2 pb-3">
          <div
            className="col-span-12 flex h-10 cursor-pointer justify-between pl-2"
            onClick={() => setExpanded((x) => !x)}
          >
            <span className="flex items-center truncate">
              <span className="font-semibold">{placeholder.name}</span> &nbsp;&nbsp;
              {placeholder.example}
            </span>
            <div className="flex gap-1">
              <DeleteIcon onClick={onRemove} />
              <ExpandArrows expanded={expanded} />
            </div>
          </div>
          <PaziaInput
            name={`${FORM_NAMES.PLACEHOLDERS}[${index}].name`}
            label="Nazwa"
            type="text"
            // onChange={(x) => changeName(x.target.value, index)}
            className="col-span-6"
          />
          {+placeholder.type == DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES.TEXT && <TextPlaceholder index={index} />}
          {+placeholder.type == DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES.IMAGE && <ImagePlaceholder index={index} />}
          {+placeholder.type == DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES.HTML && <HtmlPlaceholder index={index} />}
        </div>
      ) : (
        <div className="col-span-12 flex cursor-pointer justify-between pl-4" onClick={() => setExpanded((x) => !x)}>
          <span className="flex items-center truncate">
            <span className="font-semibold">{placeholder.name}</span> &nbsp;&nbsp;
            {placeholder.example}
          </span>
          <ExpandArrows expanded={placeholder.expanded} />
        </div>
      )}
    </div>
  );
};

const HtmlPlaceholder = ({ index }: TextPlaceholderProps) => {
  return (
    <>
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].left`}
        label="Pozycja X"
        type="number"
        className="col-span-3 row-start-3"
      />
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].top`}
        label="Pozycja Y"
        type="number"
        className="col-span-3 row-start-3"
      />
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].width`}
        label="Szerokość"
        type="number"
        className="col-span-3 row-start-3"
      />
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].height`}
        label="Wysokość"
        type="number"
        className="col-span-3 row-start-3"
      />
    </>
  );
};
interface TextPlaceholderProps {
  index: number;
}

const TextPlaceholder = ({ index }: TextPlaceholderProps) => {
  return (
    <>
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].example`}
        label="Przykładowa wartość"
        type="text"
        className="col-span-6"
      />
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].left`}
        label="Pozycja X"
        type="number"
        className="col-span-2"
      />
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].top`}
        label="Pozycja Y"
        type="number"
        className="col-span-2"
      />
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].width`}
        label="Szerokość"
        type="number"
        className="col-span-2"
      />
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].fontSize`}
        label="Rozmiar"
        type="number"
        className="col-span-3"
      />
      <PaziaColorPill label="Kolor" name={`${FORM_NAMES.PLACEHOLDERS}[${index}].color`} />

      <PaziaSelect
        options={[
          { value: "calibri", label: "Calibri" },
          { value: "centurygothic", label: "Century Gothic" },
          { value: "centurygothicbold", label: "Century Gothic Bold" },
          { value: "freesans", label: "FreeSans" },
          { value: "timesnewroman", label: "Times New Roman" },
          { value: "timesnewromanbold", label: "Times New Roman Bold" },
          { value: "tahoma", label: "Tahoma" },
          { value: "montserrat", label: "Montserrat-Regular" },
          { value: "montserrat-semibold", label: "Montserrat-SemiBold" },
          { value: "montserrat-bold", label: "Montserrat-Bold" },
          { value: "montserrat-extrabold", label: "Montserrat-ExtraBold" },
        ]}
        label="Font"
        className="col-span-6"
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].fontFamily`}
      />
      {/* <div className="flex items-end justify-center">
        <PaziaBooleanIcon name={`${FORM_NAMES.PLACEHOLDERS}[${index}].fontBold`} className="col-span-1" />
      </div> */}
      {/* <PaziaSelect
options={verticalAlignmentOptions}
label="Wyrównanie pionowe"
className="col-span-4"
name={`${FORM_NAMES.PLACEHOLDERS}[${index}].verticalAlignment`}
/> */}
      <PaziaSelect
        options={textAlignmentOptions}
        label="Wyrównanie tekstu"
        className="col-span-4"
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].textAlignment`}
      />
      <div className="col-span-1 mb-1 flex flex-col items-center justify-end">
        <PaziaToggleIcon
          name={`${FORM_NAMES.PLACEHOLDERS}[${index}].uppercase`}
          enabledColor="text-blue-500"
          disabledColor="text-gray-400"
          hoverColor="hover:text-blue-600"
        >
          <MdTextFields className="h-5 w-5" size={25} />
        </PaziaToggleIcon>
      </div>
    </>
  );
};

interface ImagePlaceholderProps {
  index: number;
}
const ImagePlaceholder = ({ index }: ImagePlaceholderProps) => {
  return (
    <>
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].left`}
        label="Pozycja X"
        type="number"
        className="col-span-2"
      />
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].top`}
        label="Pozycja Y"
        type="number"
        className="col-span-2"
      />
      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].width`}
        label="Szerokość"
        type="number"
        className="col-span-2"
      />

      <PaziaInput
        name={`${FORM_NAMES.PLACEHOLDERS}[${index}].height`}
        label="Wysokość"
        type="number"
        className="col-span-2"
      />
    </>
  );
};
interface DeleteIconProps {
  onClick?: () => void;
}

const DeleteIcon = ({ onClick }: DeleteIconProps) => {
  return (
    <div className="flex h-8 w-8 cursor-pointer items-center justify-center" onClick={onClick}>
      <svg className="slate-200 h-3 w-3 shrink-0 fill-current" viewBox="0 0 16 16">
        <path d="M5 7h2v6H5V7zm4 0h2v6H9V7zm3-6v2h4v2h-1v10c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V5H0V3h4V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1zM6 2v1h4V2H6zm7 3H3v9h10V5z" />
      </svg>
    </div>
  );
};

// TODO Move it somewhere
export const DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES = {
  TEXT: 1,
  IMAGE: 2,
  HTML: 3,
};

export const defaultImagePlaceholder: DocumentTemplatePlaceholderApi = {
  type: DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES.IMAGE,
  width: "100",
  height: "100",
  name: "",
  left: "0",
  top: "0",
  color: "",
  example: "https://greenwaystorageaccount.blob.core.windows.net/certificates/image-placeholder.png",
  // @ts-ignore
  expanded: true,
  fontBold: false,
  uppercase: false,
  fontFamily: "",
  fontSize: "0",
  textAlignment: 0,
  verticalAlignment: 0,
};
export const defaultHtmlPlaceholder: DocumentTemplatePlaceholderApi = {
  id: 0,
  type: DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES.HTML,
  width: "200",
  height: "200",
  fontSize: "20",
  color: "",
  fontBold: false,
  uppercase: false,
  verticalAlignment: 0,
  textAlignment: 1,
  fontFamily: "",
  name: "Nowy placeholder html",
  example: "Przykładowa wartość HTML",
  left: "50",
  top: "50",
};
export const defaultPlaceholder: DocumentTemplatePlaceholderApi = {
  id: 0,
  type: DOCUMENT_TEMPLATE_PLACEHOLDER_TYPES.TEXT,
  width: "120",
  height: "20",
  fontSize: "10",
  color: "rgba(0,0,0,1)",
  fontBold: false,
  uppercase: false,
  verticalAlignment: 0,
  textAlignment: 1,
  fontFamily: `"Times New Roman", Times, serif`,
  name: "Nowy placeholder",
  example: "Przykładowa wartość",
  left: "50",
  top: "50",
};
const FORM_NAMES = {
  PAGE_ID: "pageId",
  PAGE_NUMBER: "pageNumber",
  BACKGROUND: "background",
  PLACEHOLDERS: "placeholders",
} as const;

const PlaceholderSchema = yup.object({
  expanded: yup.boolean(),
  // nullable
  id: yup.number().nullable(),
  example: yup.string().nullable(),
  color: yup.string().nullable(),
  fontFamily: yup.string().nullable(),
  fontSize: yup.number().nullable(),
  fontBold: yup.boolean().nullable(),
  uppercase: yup.boolean().nullable(),
  verticalAlignment: yup.number().nullable(),
  textAlignment: yup.number().nullable(),
  // required
  name: yup.string().required(),
  type: yup.string().required(),
  width: yup.number().required(),
  height: yup.number().required(),
  left: yup.number().required(),
  top: yup.number().required(),
});

const schema = yup
  .object({
    [FORM_NAMES.BACKGROUND]: yup.mixed().required(),
    [FORM_NAMES.PAGE_ID]: yup.number().nullable(),
    [FORM_NAMES.PAGE_NUMBER]: yup.number().required(),
    [FORM_NAMES.PLACEHOLDERS]: yup.array().of(PlaceholderSchema).required(),
  })
  .required();

export type PlacehodlerFormValues = yup.InferType<typeof PlaceholderSchema>;
export type PageFormValues = yup.InferType<typeof schema>;

const verticalAlignmentOptions = [
  { value: 0, label: "Góra" },
  { value: 1, label: "Środek" },
  { value: 2, label: "Dół" },
];

const textAlignmentOptions = [
  { value: 0, label: "Lewo" },
  { value: 1, label: "Środek" },
  { value: 2, label: "Prawo" },
  // { value: 3, label: "Wyjustowany" },
  // { value: 4, label: "Wyjustowany do lewej" },
];
